import type { InsightsMode } from '@/components/insights/insights-filter-bar'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod/v4'
import {
  downloadInsightsExport,
  insightsByMachineQueryOptions,
  insightsByProductQueryOptions,
  insightsDailyQueryOptions,
  insightsHourlyQueryOptions,
  insightsMachineTypesQueryOptions,
  insightsMonthlyQueryOptions,
} from '@/api/resources/insights'
import { locationsQueryOptions } from '@/api/resources/locations'
import { InsightsPage } from '@/components/insights/insights-page'
import { useOrganisationFilter } from '@/hooks/use-organisation-filter'
import { useOrganisationFilterStore } from '@/stores/organisation-filter-store'

function toLocalDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function computeDefaultDateFrom(): string {
  const today = new Date()
  return toLocalDateString(new Date(today.getFullYear(), today.getMonth() - 5, 1))
}

function computeDefaultDateTo(): string {
  return toLocalDateString(new Date())
}

const insightsSearchSchema = z.object({
  date_from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  date_to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  location_id: z.array(z.number()).optional(),
  machine_type: z.array(z.string()).optional(),
  mode: z.enum(['transactions', 'revenue']).optional(),
})

export const Route = createFileRoute('/_app/insights')({
  head: () => ({ meta: [{ title: 'Insights - MyJunea' }] }),
  validateSearch: insightsSearchSchema,
  component: InsightsRoute,
})

function InsightsRoute(): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const [isExporting, setIsExporting] = useState(false)
  const {
    date_from,
    date_to,
    location_id: locationIds,
    machine_type: machineTypeIds,
    mode,
  } = Route.useSearch()
  const organisationFilter = useOrganisationFilter()

  const defaultDateFrom = computeDefaultDateFrom()
  const defaultDateTo = computeDefaultDateTo()

  const dateFrom = date_from ?? defaultDateFrom
  const dateTo = date_to ?? defaultDateTo

  const filterParams = {
    ...organisationFilter,
    ...(locationIds !== undefined && locationIds.length > 0 ? { location_id: locationIds } : {}),
    ...(machineTypeIds !== undefined && machineTypeIds.length > 0
      ? { machine_type: machineTypeIds }
      : {}),
    date_from: dateFrom,
    date_to: dateTo,
  }

  const monthly = useQuery({
    ...insightsMonthlyQueryOptions(filterParams),
    placeholderData: keepPreviousData,
  })
  const daily = useQuery({
    ...insightsDailyQueryOptions(filterParams),
    placeholderData: keepPreviousData,
  })
  const product = useQuery({
    ...insightsByProductQueryOptions(filterParams),
    placeholderData: keepPreviousData,
  })
  const machine = useQuery({
    ...insightsByMachineQueryOptions(filterParams),
    placeholderData: keepPreviousData,
  })
  const hourly = useQuery({
    ...insightsHourlyQueryOptions(filterParams),
    placeholderData: keepPreviousData,
  })

  const { data: locations, isLoading: isLocationsLoading } = useQuery({
    ...locationsQueryOptions(organisationFilter),
    staleTime: 5 * 60 * 1000,
  })
  const { data: machineTypes, isLoading: isMachineTypesLoading } = useQuery({
    ...insightsMachineTypesQueryOptions(organisationFilter),
    staleTime: 5 * 60 * 1000,
  })

  const chartStates = {
    monthly: { isLoading: monthly.isLoading, isFetching: monthly.isFetching },
    daily: { isLoading: daily.isLoading, isFetching: daily.isFetching },
    product: { isLoading: product.isLoading, isFetching: product.isFetching },
    machine: { isLoading: machine.isLoading, isFetching: machine.isFetching },
    hourly: { isLoading: hourly.isLoading, isFetching: hourly.isFetching },
  }

  const selectedOrgIds = useOrganisationFilterStore((s) => s.selectedOrganisationIds)
  const clearOrgFilter = useOrganisationFilterStore((s) => s.clearFilter)

  const hasActiveFilters =
    selectedOrgIds.length > 0 ||
    (date_from !== undefined && date_from !== defaultDateFrom) ||
    (date_to !== undefined && date_to !== defaultDateTo) ||
    (locationIds !== undefined && locationIds.length > 0) ||
    (machineTypeIds !== undefined && machineTypeIds.length > 0)

  function handleDateRangeChange(newDateFrom: string, newDateTo: string): void {
    void navigate({
      search: (prev) => ({
        ...prev,
        date_from: newDateFrom,
        date_to: newDateTo,
      }),
    })
  }

  function handleLocationChange(ids: number[]): void {
    void navigate({
      search: (prev) => ({ ...prev, location_id: ids.length > 0 ? ids : undefined }),
    })
  }

  function handleMachineTypeChange(types: string[]): void {
    void navigate({
      search: (prev) => ({ ...prev, machine_type: types.length > 0 ? types : undefined }),
    })
  }

  function handleModeChange(newMode: InsightsMode): void {
    void navigate({
      search: (prev) => ({ ...prev, mode: newMode === 'transactions' ? undefined : newMode }),
    })
  }

  function handleClearFilters(): void {
    clearOrgFilter()
    void navigate({ search: (prev) => ({ mode: prev.mode }) })
  }

  function handleExport(): void {
    setIsExporting(true)
    void downloadInsightsExport(filterParams).finally(() => {
      setIsExporting(false)
    })
  }

  const showRevenue = monthly.data?.some((item) => item.revenue > 0) ?? false

  return (
    <InsightsPage
      showRevenue={showRevenue}
      mode={mode ?? 'transactions'}
      onModeChange={handleModeChange}
      monthlyData={monthly.data ?? []}
      dailyData={daily.data ?? []}
      productData={product.data ?? []}
      machineData={machine.data ?? []}
      hourlyData={hourly.data ?? []}
      chartStates={chartStates}
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateRangeChange={handleDateRangeChange}
      locations={locations ?? []}
      selectedLocationIds={locationIds ?? []}
      onLocationChange={handleLocationChange}
      isLocationsLoading={isLocationsLoading}
      machineTypes={machineTypes ?? []}
      selectedMachineTypes={machineTypeIds ?? []}
      onMachineTypeChange={handleMachineTypeChange}
      isMachineTypesLoading={isMachineTypesLoading}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      onExport={handleExport}
      isExporting={isExporting}
    />
  )
}
